import React from 'react'
import PropTypes from 'prop-types'
import { useFeatureFlag } from '@fs/zion-flags'
import { FamilyMembersCard } from '../familyMembersCard/FamilyMembersCard'
import { RelationshipsCard } from './RelationshipsCard'

// REMOVE THIS FILE when `rip_sharedImageViewer_relationshipsCard` is cleaned up: delete it along
// with `familyMembersCard/`, and point the panels straight at RelationshipsCard.

/**
 * Whether the reworked card is the one to render. The single read of the rollout flag.
 *
 * @returns {boolean} Whether the flag is on.
 */
export const useRelationshipsCardIsOn = () => Boolean(useFeatureFlag('rip_sharedImageViewer_relationshipsCard')?.isOn)

/**
 * Whether a relationships section renders at all. Also used by panels that route their stepper past
 * it, so the two cannot disagree.
 *
 * @param {boolean} [householdHasOtherPersons=false] Whether the person's record holds anyone else.
 * @param {boolean} [isCreatingNewPerson=false] Whether the person is being created rather than edited.
 * @returns {boolean} Whether the section renders.
 */
export const useRelationshipsSectionVisible = (householdHasOtherPersons = false, isCreatingNewPerson = false) => {
  const relationshipsCardIsOn = useRelationshipsCardIsOn()

  // A person being created has nobody to relate to until the record holds somebody else.
  if (isCreatingNewPerson) return householdHasOtherPersons

  return relationshipsCardIsOn || householdHasOtherPersons
}

/**
 * Renders whichever relationships card the rollout flag selects. Every other prop forwards untouched.
 *
 * @component
 * @param {Object} props
 * @param {boolean} [props.householdHasOtherPersons=false] Whether the person's record holds anyone else.
 * @param {boolean} [props.isCreatingNewPerson=false] Whether the person is being created rather than edited.
 * @returns {JSX.Element|null}
 */
const RelationshipsCardSwitch = ({ householdHasOtherPersons = false, isCreatingNewPerson = false, ...cardProps }) => {
  const relationshipsCardIsOn = useRelationshipsCardIsOn()
  // Same rule as useRelationshipsSectionVisible, which panels use to route their stepper past this card.
  const sectionVisible = useRelationshipsSectionVisible(householdHasOtherPersons, isCreatingNewPerson)

  if (!sectionVisible) return null

  return relationshipsCardIsOn ? (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <RelationshipsCard {...cardProps} isCreatingNewPerson={isCreatingNewPerson} />
  ) : (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FamilyMembersCard {...cardProps} isCreatingNewPerson={isCreatingNewPerson} />
  )
}

RelationshipsCardSwitch.propTypes = {
  householdHasOtherPersons: PropTypes.bool,
  isCreatingNewPerson: PropTypes.bool,
}

RelationshipsCardSwitch.defaultProps = {
  householdHasOtherPersons: false,
  isCreatingNewPerson: false,
}

export default RelationshipsCardSwitch
